import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const links = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About' },
  { to: '/courses', label: 'Programmes' },
  { to: '/enroll', label: 'Enroll' },
  { to: '/gallery', label: 'Gallery' },
];

const Header: React.FC = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Close the overlay on route change, and lock body scroll while it's open.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <header className="pointer-events-none fixed inset-x-0 top-0 z-50 mt-5 px-4 sm:mt-6">
      {/* Floating glass island — detached from the top edge, width-to-content. */}
      {/* `relative z-50` keeps the island above the z-40 overlay below — they
          share the header's stacking context, so without it the overlay paints
          over the logo and the morphing hamburger. */}
      <div
        className={`pointer-events-auto relative z-50 mx-auto flex w-full max-w-4xl items-center justify-between gap-4 rounded-full py-2 pl-2 pr-2 transition-all duration-700 ease-fluid sm:w-max sm:gap-8 sm:pl-3 sm:pr-3 ${
          open
            ? 'border border-transparent bg-transparent shadow-none'
            : 'border border-espresso-700/[0.07] bg-cream/70 shadow-ambient backdrop-blur-2xl'
        }`}
      >
        <Link to="/" aria-label="Leolilian Care International — home" className="shrink-0">
          <img
            src="/img/Logo.png"
            alt="Leolilian Care International"
            className="h-10 w-auto rounded-full transition-transform duration-700 ease-fluid hover:scale-[1.04]"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden sm:block">
          <ul className="flex items-center gap-1">
            {links.map((link) => {
              const active = location.pathname === link.to;
              return (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className={`relative rounded-full px-4 py-2 text-[13px] font-medium tracking-tight transition-all duration-700 ease-fluid ${
                      active
                        ? 'bg-espresso-800 text-cream'
                        : 'text-espresso-600 hover:bg-espresso-700/[0.05] hover:text-espresso-900'
                    }`}
                  >
                    {link.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Hamburger — two lines that rotate into a true X */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-espresso-700/[0.05] transition-all duration-700 ease-fluid active:scale-[0.94] sm:hidden"
        >
          <span
            className={`absolute h-px w-[18px] bg-espresso-800 transition-all duration-700 ease-fluid ${
              open ? 'translate-y-0 rotate-45' : '-translate-y-[3px] rotate-0'
            }`}
          />
          <span
            className={`absolute h-px w-[18px] bg-espresso-800 transition-all duration-700 ease-fluid ${
              open ? 'translate-y-0 -rotate-45' : 'translate-y-[3px] rotate-0'
            }`}
          />
        </button>
      </div>

      {/* Full-bleed glass overlay with staggered mask reveal */}
      <div
        className={`fixed inset-0 z-40 bg-cream/80 backdrop-blur-3xl transition-all duration-700 ease-fluid sm:hidden ${
          open ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
        }`}
      >
        <nav className="flex h-full flex-col justify-center px-8">
          <ul className="space-y-2">
            {links.map((link, i) => (
              <li
                key={link.to}
                className={`overflow-hidden transition-all duration-700 ease-fluid ${
                  open ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'
                }`}
                style={{ transitionDelay: open ? `${120 + i * 60}ms` : '0ms' }}
              >
                <Link
                  to={link.to}
                  className="block font-display text-5xl font-light tracking-tight text-espresso-800 transition-colors duration-700 ease-fluid hover:text-sage-deep"
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>

          <div
            className={`mt-12 transition-all duration-700 ease-fluid ${
              open ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'
            }`}
            style={{ transitionDelay: open ? `${120 + links.length * 60}ms` : '0ms' }}
          >
            <p className="text-[10px] uppercase tracking-eyebrow text-espresso-500">
              Germiston, South Africa
            </p>
            <a
              href="mailto:admin@leolilly.org"
              className="mt-2 inline-block text-sm text-espresso-600 underline decoration-espresso-700/20 underline-offset-4"
            >
              admin@leolilly.org
            </a>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
